export function fmtProb(p: any): string {
  if (typeof p !== 'number') return '-'
  return `${(p * 100).toFixed(1)}%`
}

/** Probability as an integer percent (e.g. 0.456 -> "46%"). Used for
 *  compact tiles and cockpit cards where one-decimal precision is noise. */
export function fmtProbInt(p: any): string {
  if (typeof p !== 'number') return '-'
  return `${Math.round(p * 100)}%`
}

/** Generic 0..1 score as percent. Default integer precision; pass
 *  `{ decimals: 1 }` for one decimal. */
export function fmtPct(value: any, opts?: { decimals?: number }): string {
  if (typeof value !== 'number') return '-'
  const d = opts?.decimals ?? 0
  return `${(value * 100).toFixed(d)}%`
}

export function fmtEdge(e: any): string {
  if (typeof e !== 'number') return '-'
  const s = e >= 0 ? '+' : ''
  return `${s}${(e * 100).toFixed(1)}%`
}

export function timeAgo(iso: any): string {
  if (!iso) return '-'
  const d = new Date(String(iso))
  if (Number.isNaN(d.getTime())) return String(iso)
  const ms = Date.now() - d.getTime()
  const mins = Math.floor(ms / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 48) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

export function fmtTs(iso: any): string {
  if (!iso) return '-'
  const d = new Date(String(iso))
  if (Number.isNaN(d.getTime())) return String(iso)
  return d.toLocaleString()
}

/**
 * Pick the freshest valid ISO timestamp from a list of candidates.
 * Returns `null` when nothing parses to a finite date. Used by detail
 * heros to surface a single "last updated" signal computed from several
 * payload fields that may or may not be present.
 */
export function freshest(...candidates: Array<string | null | undefined>): string | null {
  let bestMs = -Infinity
  let bestIso: string | null = null
  for (const iso of candidates) {
    if (!iso) continue
    const d = new Date(String(iso))
    const t = d.getTime()
    if (!Number.isFinite(t)) continue
    if (t > bestMs) {
      bestMs = t
      bestIso = String(iso)
    }
  }
  return bestIso
}

/**
 * Source snippets are scraped page heads: they open with the page title,
 * nav chrome ("Menu CLOSE", "Skip to main content", ticker widgets), then
 * the real prose. Strip the chrome and the title echo so the card shows
 * article text, not website furniture.
 */
export function cleanSourceSnippet(raw: any, title?: any, maxLen: number = 180): string {
  let text = cleanSnippet(raw, 100000)
  if (!text) return ''
  const t = String(title || '').replace(/\s+/g, ' ').trim()
  // The article body often restarts at a later repeat of the title — prefer it.
  if (t) {
    const lower = text.toLowerCase()
    const tl = t.toLowerCase()
    const second = lower.indexOf(tl, lower.startsWith(tl) ? t.length : 0)
    if (second >= 0) text = text.slice(second + t.length)
    else if (lower.startsWith(tl)) text = text.slice(t.length)
  }
  text = text
    .replace(/skip to main content/gi, ' ')
    .replace(/continue to site\s*[➞→»]*/gi, ' ')
    .replace(/accessibility menu/gi, ' ')
    .replace(/\bmenu\s+close\b/gi, ' ')
    .replace(/[▲▼]\s*[A-Z&]{1,8}\s*\d*\s*[+-]+-*%?\|?/g, ' ')
    .replace(/\+---%\|?/g, ' ')
    .replace(/^[\s\W_]+/, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
  if (text.length > maxLen) text = text.slice(0, maxLen) + '…'
  return text
}

/**
 * Older stored monitor rules carry literal template placeholders
 * ("flag any new entrant ... for {name} ({ticker})"). Fill them from the
 * row's entity context; drop any placeholder we can't resolve.
 */
export function fillEntityPlaceholders(raw: any, name?: any, ticker?: any): string {
  let text = String(raw || '')
  if (!text.includes('{')) return text
  const n = String(name || '').trim()
  const t = String(ticker || '').trim()
  if (n) text = text.split('{name}').join(n).split('{company}').join(n).split('{entity}').join(n)
  if (t) text = text.split('{ticker}').join(t)
  return text
    .replace(/\s*\(\{ticker\}\)/g, '')
    .replace(/\s*\{[a-zA-Z_][a-zA-Z0-9_]*\}/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

export function fmtDelta(delta: number | null | undefined): { text: string; cls: string } | null {
  if (typeof delta !== 'number') return null
  const sign = delta >= 0 ? '+' : ''
  return {
    text: `${sign}${(delta * 100).toFixed(1)}%`,
    cls: delta > 0 ? 'delta-up' : delta < 0 ? 'delta-down' : 'delta-flat',
  }
}

/** Clean raw web-scrape snippets for UI display. */
export function cleanSnippet(raw: any, maxLen: number = 180): string {
  if (!raw) return ''
  // Handle arrays (some sources store snippets as string[])
  let text = Array.isArray(raw) ? raw.join(' ') : String(raw)
  // Strip HTML tags (search-result snippets arrive with <strong> highlights)
  text = text.replace(/<[^>]+>/g, '')
  // Strip leading/trailing array brackets that got stringified
  text = text.replace(/^\[['"]?|['"]?\]$/g, '')
  // Strip markdown image refs: ![alt text](url) or ![alt text]
  text = text.replace(/!\[([^\]]*)\]\([^)]*\)/g, '')
  text = text.replace(/!\[([^\]]*)\]/g, '')
  // Strip markdown links but keep text: [text](url) -> text
  text = text.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
  // Strip remaining markdown brackets
  text = text.replace(/\[([^\]]*)\]/g, '$1')
  // Normalize whitespace: literal \n, multiple spaces, etc.
  text = text.replace(/\\n/g, ' ')
  text = text.replace(/\n/g, ' ')
  text = text.replace(/\s{2,}/g, ' ')
  // Strip leading * bullets
  text = text.replace(/^\s*\*\s*/g, '')
  text = text.replace(/\s\*\s/g, ' ')
  text = text.trim()
  if (text.length > maxLen) text = text.slice(0, maxLen) + '…'
  return text
}
